import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { HiArrowRight, HiMail, HiCalendar, HiSparkles } from 'react-icons/hi';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSectionV2 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section-v2"
      className="py-24 md:py-32 bg-gradient-to-br from-[#5B8FB9] via-[#6BA897] to-[#7CB98F] relative overflow-hidden"
    >
      {/* Animated decorative elements */}
      <motion.div 
        animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute top-0 right-0 w-96 h-96 bg-[#D4A5B5]/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" 
      />
      <motion.div 
        animate={{ scale: [1.2, 1, 1.2] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" 
      />
      <motion.div 
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/3 left-1/4 w-32 h-32 bg-[#D4A5B5]/20 rounded-full blur-xl" 
      />
      
      {/* Floating circles */}
      <motion.div 
        animate={{ y: [0, -30, 0], x: [0, 15, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute top-20 right-1/4 w-8 h-8 bg-white/20 rounded-full" 
      />
      <motion.div 
        animate={{ y: [0, 20, 0], x: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity }}
        className="absolute bottom-1/3 right-20 w-6 h-6 bg-[#D4A5B5]/40 rounded-full" 
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - CTA Text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <motion.span 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur text-white text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-6 border border-white/30"
            >
              <HiSparkles className="w-4 h-4" />
              Get Started
            </motion.span>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-tight text-white mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="contact-headline-v2"
            >
              Ready to Build a More Sustainable Way of Leading?
            </h2>
            <p className="text-lg text-white/80 font-light mb-10" data-testid="contact-subheadline-v2">
              If you're looking to improve performance, strengthen leadership, and 
              create a healthier work environment, let's connect.
            </p>

            {/* Quick Options */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <motion.a
                whileHover={{ scale: 1.05 }}
                href="#calendly"
                className="inline-flex items-center gap-3 px-6 py-4 bg-white/20 backdrop-blur border border-white/30 rounded-full text-white hover:bg-white/30 transition-colors"
                data-testid="contact-calendly-link-v2"
              >
                <HiCalendar className="w-5 h-5" />
                <span>Schedule via Calendly</span>
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05 }}
                href="mailto:hello@susana.com"
                className="inline-flex items-center gap-3 px-6 py-4 bg-white/20 backdrop-blur border border-white/30 rounded-full text-white hover:bg-white/30 transition-colors"
                data-testid="contact-email-link-v2"
              >
                <HiMail className="w-5 h-5" />
                <span>hello@susana.com</span>
              </motion.a>
            </div>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white/95 backdrop-blur-sm p-8 md:p-10 rounded-[2rem] shadow-2xl"
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form-v2">
              <div>
                <label htmlFor="name" className="block text-sm text-gray-600 mb-2 font-medium">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-full text-gray-900 placeholder-gray-400 transition-all duration-300 focus:border-[#5B8FB9] focus:ring-2 focus:ring-[#5B8FB9]/20 outline-none"
                  placeholder="John Doe"
                  data-testid="contact-input-name-v2"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm text-gray-600 mb-2 font-medium">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-full text-gray-900 placeholder-gray-400 transition-all duration-300 focus:border-[#7CB98F] focus:ring-2 focus:ring-[#7CB98F]/20 outline-none"
                  placeholder="john@company.com"
                  data-testid="contact-input-email-v2"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm text-gray-600 mb-2 font-medium">
                  Company (Optional)
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-full text-gray-900 placeholder-gray-400 transition-all duration-300 focus:border-[#D4A5B5] focus:ring-2 focus:ring-[#D4A5B5]/20 outline-none"
                  placeholder="Your Company"
                  data-testid="contact-input-company-v2"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm text-gray-600 mb-2 font-medium">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-3xl text-gray-900 placeholder-gray-400 transition-all duration-300 focus:border-[#5B8FB9] focus:ring-2 focus:ring-[#5B8FB9]/20 outline-none resize-none"
                  placeholder="Tell me about your challenges and goals..."
                  data-testid="contact-input-message-v2"
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#5B8FB9] via-[#7CB98F] to-[#5B8FB9] bg-[length:200%_100%] text-white font-medium rounded-full transition-all duration-500 hover:bg-right shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-button-v2"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                {!isSubmitting && <HiArrowRight className="w-5 h-5" />}
              </motion.button>

              {submitStatus === 'success' && (
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-[#7CB98F] text-center font-medium" 
                  data-testid="contact-success-message-v2"
                >
                  Thank you! Your message has been sent. I'll get back to you soon.
                </motion.p>
              )}

              {submitStatus === 'error' && (
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-[#D4A5B5] text-center font-medium" 
                  data-testid="contact-error-message-v2"
                >
                  Something went wrong. Please try again or email directly.
                </motion.p>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSectionV2;
